@if (loading()) {
  <div class="space-y-5 animate-pulse" role="status" aria-label="Cargando promoción">
    <div class="h-24 w-full rounded-xl bg-gray-100 dark:bg-gray-800/70"></div>
    <div class="h-11 w-full rounded-lg bg-gray-200 dark:bg-gray-800"></div>
    <div class="h-20 w-full rounded-lg bg-gray-100 dark:bg-gray-800/70"></div>
  </div>
} @else {
  <form [formGroup]="form" class="pf" novalidate>

    <!-- Discount + live coupon preview -->
    <section class="pf-section" aria-labelledby="pf-discount">
      <h3 id="pf-discount" class="pf-section__title">Descuento</h3>

      <div class="pf-discount">
        <div class="pf-preview" [attr.data-type]="form.controls.discountType.value" aria-hidden="true">
          <span class="pf-preview__value">{{ previewValue() }}</span>
          <span class="pf-preview__unit">{{ form.controls.discountType.value === 'percentage' ? 'de descuento' : 'menos' }}</span>
        </div>

        <div class="grid min-w-0 gap-3">
          <div class="pf-segment" role="radiogroup" aria-label="Tipo de descuento">
            <label class="pf-segment__option">
              <input type="radio" formControlName="discountType" value="percentage" class="sr-only" />
              <span>Porcentaje</span>
            </label>
            <label class="pf-segment__option">
              <input type="radio" formControlName="discountType" value="fixed" class="sr-only" />
              <span>Monto fijo</span>
            </label>
          </div>

          <div>
            <label for="pf-value" class="pf-label">
              {{ form.controls.discountType.value === 'percentage' ? 'Porcentaje de descuento' : 'Pesos de descuento' }}
              <span class="pf-req" aria-hidden="true">*</span>
            </label>
            <div class="relative">
              @if (form.controls.discountType.value === 'fixed') {
                <span class="pf-affix pf-affix--left" aria-hidden="true">$</span>
              }
              <input id="pf-value" type="number" inputmode="numeric" formControlName="value" min="1"
                [attr.max]="form.controls.discountType.value === 'percentage' ? 100 : null"
                class="pf-input" [class.pf-input--prefixed]="form.controls.discountType.value === 'fixed'" [class.pf-input--suffixed]="form.controls.discountType.value === 'percentage'"
                [class.pf-input--error]="showError('value')"
                aria-required="true" [attr.aria-invalid]="showError('value')"
                [attr.aria-describedby]="showError('value') ? 'pf-value-error' : null" />
              @if (form.controls.discountType.value === 'percentage') {
                <span class="pf-affix pf-affix--right" aria-hidden="true">%</span>
              }
            </div>
            @if (showError('value')) {
              <p id="pf-value-error" class="pf-error" role="alert">El descuento debe ser de al menos 1.</p>
            }
          </div>
        </div>
      </div>
    </section>

    <!-- Name + description -->
    <section class="pf-section" aria-labelledby="pf-basics">
      <h3 id="pf-basics" class="pf-section__title">Cómo la verán tus clientes</h3>

      <div>
        <label for="pf-name" class="pf-label">Nombre <span class="pf-req" aria-hidden="true">*</span></label>
        <input id="pf-name" type="text" formControlName="name" placeholder="Ej: Martes de uñas"
          class="pf-input" [class.pf-input--error]="showError('name')"
          aria-required="true" [attr.aria-invalid]="showError('name')"
          [attr.aria-describedby]="showError('name') ? 'pf-name-error' : null" />
        @if (showError('name')) {
          <p id="pf-name-error" class="pf-error" role="alert">Escribe un nombre de al menos 4 caracteres.</p>
        }
      </div>

      <div>
        <label for="pf-description" class="pf-label">Descripción <span class="pf-req" aria-hidden="true">*</span></label>
        <textarea id="pf-description" rows="3" formControlName="description"
          placeholder="Ej: Todos los martes, 20 % en manicure semipermanente."
          class="pf-input pf-input--area" [class.pf-input--error]="showError('description')"
          aria-required="true" [attr.aria-invalid]="showError('description')"
          [attr.aria-describedby]="showError('description') ? 'pf-description-error' : null"></textarea>
        @if (showError('description')) {
          <p id="pf-description-error" class="pf-error" role="alert">Explica en una frase cómo funciona la promoción.</p>
        }
      </div>
    </section>

    <!-- Scope -->
    <section class="pf-section" aria-labelledby="pf-scope">
      <h3 id="pf-scope" class="pf-section__title">Dónde aplica</h3>

      <div class="pf-toggle">
        <div class="min-w-0">
          <p id="pf-all-label" class="pf-toggle__title">Todos los servicios</p>
          <p class="pf-toggle__hint">
            {{ allServices() ? 'El descuento aplica a cualquier servicio.' : 'Elige abajo los servicios con descuento.' }}
          </p>
        </div>
        <button type="button" role="switch" class="pf-switch" [attr.aria-checked]="allServices()"
          aria-labelledby="pf-all-label" (click)="onAllServicesToggle(!allServices())">
          <span class="pf-switch__knob" aria-hidden="true"></span>
        </button>
      </div>

      @if (!allServices()) {
        <app-service-chip-selector
          formControlName="services"
          label="Servicios con descuento"
          [required]="true"
          placeholder="Busca y elige servicios">
        </app-service-chip-selector>
      }

      <fieldset>
        <legend class="pf-label">Para quién</legend>
        <div class="pf-pills">
          @for (option of audienceOptions; track option.value) {
            <label class="pf-pill">
              <input type="radio" formControlName="audience" [value]="option.value" class="sr-only" />
              <span>{{ option.label }}</span>
            </label>
          }
        </div>
      </fieldset>
    </section>

    <!-- When + channels -->
    <section class="pf-section" aria-labelledby="pf-when">
      <h3 id="pf-when" class="pf-section__title">Cuándo y por dónde</h3>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label for="pf-start" class="pf-label">Empieza <span class="pf-req" aria-hidden="true">*</span></label>
          <input #startDateInput id="pf-start" type="text" formControlName="startDate" placeholder="Elige una fecha" readonly
            autocomplete="off" class="pf-input cursor-pointer" [class.pf-input--error]="showError('startDate')"
            aria-required="true" [attr.aria-invalid]="showError('startDate')" />
          @if (showError('startDate')) {
            <p class="pf-error" role="alert">Elige el día en que empieza.</p>
          }
        </div>
        <div>
          <label for="pf-end" class="pf-label">Termina <span class="pf-req" aria-hidden="true">*</span></label>
          <input #endDateInput id="pf-end" type="text" formControlName="endDate" placeholder="Elige una fecha" readonly
            autocomplete="off" class="pf-input cursor-pointer" [class.pf-input--error]="showError('endDate')"
            aria-required="true" [attr.aria-invalid]="showError('endDate')" />
          @if (showError('endDate')) {
            <p class="pf-error" role="alert">Elige el día en que termina.</p>
          }
        </div>
      </div>

      <fieldset formGroupName="channels">
        <legend class="pf-label">Anunciar por</legend>
        <div class="pf-pills">
          @for (channel of channelOptions; track channel.value) {
            <label class="pf-pill pf-pill--check">
              <input type="checkbox" [formControlName]="channel.value" class="sr-only" />
              <svg class="pf-pill__check size-4" fill="none" viewBox="0 0 24 24" stroke-width="2.25" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
              <span>{{ channel.label }}</span>
            </label>
          }
        </div>
      </fieldset>

      <div class="sm:max-w-[50%]">
        <label for="pf-goal" class="pf-label">Meta de usos <span class="pf-optional">opcional</span></label>
        <input id="pf-goal" type="number" inputmode="numeric" min="0" formControlName="goal" placeholder="Ej: 50"
          class="pf-input" aria-describedby="pf-goal-hint" />
        <p id="pf-goal-hint" class="pf-hint">Cuántas veces esperas que se use. Te mostramos el avance.</p>
      </div>
    </section>
  </form>
}
